import { OidcClient, WebStorageStateStore } from 'oidc-client-ts';
import { z } from 'zod';

import { config } from '@/config/config';
import type {
  AuthIntent,
  LinkedInLoginState,
} from '@/modules/auth/types/externalLogin';
import { sanitizeReturnTo } from '@/modules/auth/utils/sanitizeReturnTo';

const linkedinAuthority = 'https://www.linkedin.com/oauth';
const linkedinAuthorizeEndpoint =
  'https://www.linkedin.com/oauth/v2/authorization';
const linkedInScope = 'openid profile email';

const linkedInStateSchema = z.object({
  flow: z.literal('linkedin'),
  returnTo: z.string(),
  // LinkedIn's redirect drops the page the user clicked on, so whether its
  // Terms Notice was shown rides in the state. Sign-ins started before this
  // field existed default to not accepting.
  acceptTerms: z.boolean().default(false),
  // Which page started the flow, so the callback can report a sign-up or a
  // sign-in. Flows started before this field existed count as sign-ins.
  intent: z.enum(['signUp', 'signIn']).default('signIn'),
});

export type LinkedInSsoErrorCode =
  | 'callback_error'
  | 'missing_code'
  | 'missing_state'
  | 'invalid_state'
  | 'invalid_state_data';

export class LinkedInSsoError extends Error {
  public readonly code: LinkedInSsoErrorCode;

  constructor(code: LinkedInSsoErrorCode, message: string) {
    super(message);
    this.code = code;
  }
}

interface CompleteLinkedInLoginResult {
  returnTo: string;
  code: string;
  acceptTerms: boolean;
  intent: AuthIntent;
}

let oidcClient: OidcClient | null = null;

const createOrGetOidcClient = () => {
  if (oidcClient) {
    return oidcClient;
  }

  oidcClient = new OidcClient({
    authority: linkedinAuthority,
    client_id: config.external.linkedinClientId,
    redirect_uri: config.external.linkedinRedirectUri,
    response_type: 'code',
    scope: linkedInScope,
    metadata: {
      issuer: 'https://www.linkedin.com',
      authorization_endpoint: linkedinAuthorizeEndpoint,
    },
    stateStore: new WebStorageStateStore({
      store: window.sessionStorage,
    }),
  });

  return oidcClient;
};

export const startLinkedInLogin = async (
  returnTo: string | undefined,
  acceptTerms: boolean,
  intent: AuthIntent,
) => {
  const client = createOrGetOidcClient();

  const state: LinkedInLoginState = {
    flow: 'linkedin',
    returnTo: sanitizeReturnTo(returnTo),
    acceptTerms,
    intent,
  };

  const signInRequest = await client.createSigninRequest({
    state,
    disablePKCE: true,
  });

  window.location.assign(signInRequest.url);
};

export const completeLinkedInLoginFromCallback = async (
  callbackUrl: string,
): Promise<CompleteLinkedInLoginResult> => {
  const callback = new URL(callbackUrl, window.location.origin);
  const searchParams = callback.searchParams;

  const callbackError = searchParams.get('error');
  if (callbackError) {
    throw new LinkedInSsoError(
      'callback_error',
      searchParams.get('error_description') ?? callbackError,
    );
  }

  if (!searchParams.get('code')) {
    throw new LinkedInSsoError(
      'missing_code',
      'Missing authorization code in LinkedIn callback.',
    );
  }

  if (!searchParams.get('state')) {
    throw new LinkedInSsoError(
      'missing_state',
      'Missing state in LinkedIn callback.',
    );
  }

  const client = createOrGetOidcClient();
  const { response, state } = await client.readSigninResponseState(
    callback.toString(),
  );

  if (!response.code) {
    throw new LinkedInSsoError(
      'invalid_state',
      'LinkedIn callback state could not be validated.',
    );
  }

  const parsedState = linkedInStateSchema.safeParse(state.data);
  if (!parsedState.success) {
    throw new LinkedInSsoError(
      'invalid_state_data',
      'LinkedIn callback state payload is invalid.',
    );
  }

  return {
    code: response.code,
    returnTo: sanitizeReturnTo(parsedState.data.returnTo),
    acceptTerms: parsedState.data.acceptTerms,
    intent: parsedState.data.intent,
  };
};

// Reads which page started the flow without validating the callback, so a
// failed one (e.g. the user cancelled at LinkedIn) can still be reported as a
// failed sign-up or sign-in. Undefined when the stored state is gone.
export const readLinkedInIntent = async (
  callbackUrl: string,
): Promise<AuthIntent | undefined> => {
  try {
    const { state } =
      await createOrGetOidcClient().readSigninResponseState(callbackUrl);
    const parsedState = linkedInStateSchema.safeParse(state.data);
    return parsedState.success ? parsedState.data.intent : undefined;
  } catch {
    return undefined;
  }
};

export const clearLinkedInSigninState = async () => {
  await createOrGetOidcClient().clearStaleState();
};
